import type { EcsConfigIdTelemetry, EcsRolloutConfig } from 'owa-analytics-types';

// Match .NET String.Trim/Regex whitespace, not JavaScript's different \s set.
const NUMERIC_TOKEN =
    /^[^:\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+:([0-9]+)$/;
const FEATURE_FLAG_VALUE = /^0*[01]$/;
const TOKEN_EDGE_WHITESPACE =
    /^[\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+|[\u0009-\u000D\u0020\u0085\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+$/g;
export function getEcsConfigIdTelemetry(
    configIds: string,
    rolloutConfig?: EcsRolloutConfig
): EcsConfigIdTelemetry {
    const { EcsConfigIds, EcsDefaultConfigIds } = partitionEcsConfigIds(configIds);
    const entries = EcsConfigIds.split(',');
    // Only the columns emitted on this mapping can cover a residual token.
    const rollouts = new Set(rolloutConfig?.EcsRolloutIds?.split(',').map(token => `P-R-${token}`));
    const aliases = new Set(rolloutConfig?.EcsConfigAliases?.split(','));
    const tokens: string[] = [];
    const featureFlags: string[] = [];
    for (const entry of entries) {
        const token = entry.replace(TOKEN_EDGE_WHITESPACE, '');
        const match = NUMERIC_TOKEN.exec(token);
        if (match) {
            if (!FEATURE_FLAG_VALUE.test(match[1])) {
                tokens.push(token);
            } else if (!rollouts.has(entry.trim()) && !aliases.has(entry.trim())) {
                featureFlags.push(token);
            }
        }
    }
    const coveredTokens = new Set([...tokens, ...featureFlags]);
    const residual = entries.filter(entry => {
        const token = entry.trim();
        return (
            !coveredTokens.has(entry.replace(TOKEN_EDGE_WHITESPACE, '')) &&
            !rollouts.has(token) &&
            !aliases.has(token)
        );
    });
    // Preserve case: the backend uses the same ToUpperInvariant as its legacy reader.
    return {
        EcsConfigIds: residual.join(','),
        EcsScorecardFlights: tokens.join(','),
        EcsFeatureFlags: featureFlags.join(','),
        ...(EcsDefaultConfigIds !== undefined && { EcsDefaultConfigIds }),
    };
}

export function partitionEcsConfigIds(configIds: string): {
    readonly EcsConfigIds: string;
    readonly EcsDefaultConfigIds?: string;
} {
    const otherIds: string[] = [];
    const defaultIds: string[] = [];
    for (const entry of configIds.split(',')) {
        // Trim only for classification; preserve the original token and allocation fragment.
        const target = /^[pP]-[dD]-/.test(entry.trim()) ? defaultIds : otherIds;
        target.push(entry);
    }
    return {
        EcsConfigIds: otherIds.join(','),
        ...(defaultIds.length > 0 && { EcsDefaultConfigIds: defaultIds.join(',') }),
    };
}
